import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import AuthContext from '../context/AuthContext';


const userBackend = process.env.REACT_APP_USER_BACKEND_URL;
const gymBackend = process.env.REACT_APP_GYM_BACKEND_URL;

const formatDate = (value) => new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'long',
  timeStyle: 'short',
  timeZone: 'Europe/Paris',
}).format(new Date(value));

const profileUrl = (profile) => (
  profile ? `${userBackend}/media/profile/${profile}` : undefined
);

const readPayload = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json') ? response.json() : {};
};

const Comments = () => {
  const { authTokens } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [replyingId, setReplyingId] = useState(null);

  const fetchComments = useCallback(async () => {
    if (!authTokens?.access) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${gymBackend}/comments/view_comments/`, {
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
          Accept: 'application/json',
          'Accept-Language': 'fr',
        },
      });
      const payload = await readPayload(response);
      if (!response.ok) {
        throw new Error(payload.detail || 'Impossible de charger les avis.');
      }
      setComments(Array.isArray(payload.comments) ? payload.comments : []);
      window.dispatchEvent(new CustomEvent('gyma:comments-updated', {
        detail: { unseenComments: Number(payload.unseen_comments) || 0 },
      }));
    } catch (requestError) {
      setError(requestError.message || 'Impossible de charger les avis.');
    } finally {
      setLoading(false);
    }
  }, [authTokens?.access]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const submitReply = async (parentId) => {
    const content = String(replyText[parentId] || '').trim();
    if (!content) return;

    setReplyingId(parentId);
    setError('');
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('parent_comment_id', String(parentId));

      const response = await fetch(`${gymBackend}/comments/add/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authTokens.access}`,
          Accept: 'application/json',
        },
        body: formData,
      });
      const payload = await readPayload(response);
      if (!response.ok) {
        throw new Error(payload.detail || payload.content?.[0] || "La réponse n'a pas pu être publiée.");
      }
      setReplyText((current) => ({ ...current, [parentId]: '' }));
      await fetchComments();
      setExpandedId(parentId);
    } catch (requestError) {
      setError(requestError.message || "La réponse n'a pas pu être publiée.");
    } finally {
      setReplyingId(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Avis des clients</Typography>
          <Typography color="text.secondary">
            Consultez les avis vérifiés et répondez directement à vos membres.
          </Typography>
        </Box>
        <Chip
          icon={<ChatBubbleOutlineRoundedIcon />}
          label={`${comments.length} avis`}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 800 }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

      {loading ? (
        <Box display="grid" placeItems="center" minHeight={260}><CircularProgress /></Box>
      ) : comments.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 4, borderStyle: 'dashed' }}>
          <CardContent sx={{ py: 7, textAlign: 'center' }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" fontWeight={850}>Aucun avis pour le moment</Typography>
            <Typography color="text.secondary">
              Les avis des clients ayant acheté un abonnement apparaîtront ici.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {comments.map((item) => {
            const comment = item.comment;
            const writerName = [item.user_info?.first_name, item.user_info?.last_name]
              .filter(Boolean)
              .join(' ') || 'Client Gyma';
            const isExpanded = expandedId === comment.id;

            return (
              <Card
                key={comment.id}
                sx={{
                  borderRadius: 4,
                  border: comment.is_seen ? '1px solid rgba(0,4,73,0.08)' : '1px solid rgba(249,90,0,0.40)',
                  boxShadow: '0 10px 30px rgba(0,4,73,0.07)',
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar src={profileUrl(item.user_info?.profile)} sx={{ width: 48, height: 48 }}>
                      {writerName.charAt(0)}
                    </Avatar>
                    <Box minWidth={0} flexGrow={1}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        gap={1}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography fontWeight={900}>{writerName}</Typography>
                            {!comment.is_seen && <Chip size="small" color="warning" label="Nouveau" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(comment.created_at)}
                          </Typography>
                        </Box>
                        <Rating value={Number(comment.rate)} precision={0.5} readOnly size="small" />
                      </Stack>

                      <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {comment.content}
                      </Typography>

                      <Button
                        size="small"
                        startIcon={<ReplyRoundedIcon />}
                        onClick={() => setExpandedId(isExpanded ? null : comment.id)}
                        sx={{ mt: 1.5 }}
                      >
                        {isExpanded ? 'Fermer' : item.replies.length ? 'Voir la réponse' : 'Répondre'}
                      </Button>

                      <Collapse in={isExpanded} unmountOnExit>
                        <Box mt={2}>
                          {item.replies.map(({ comment: reply }) => (
                            <Box
                              key={reply.id}
                              sx={{
                                bgcolor: '#f3f5fa',
                                borderLeft: '4px solid #000449',
                                borderRadius: 2,
                                p: 2,
                                mb: 1.5,
                              }}
                            >
                              <Typography fontWeight={850}>Réponse de la salle</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(reply.created_at)}
                              </Typography>
                              <Typography mt={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {reply.content}
                              </Typography>
                            </Box>
                          ))}

                          <Divider sx={{ my: 2 }} />
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Votre réponse"
                              value={replyText[comment.id] || ''}
                              onChange={(event) => setReplyText((current) => ({
                                ...current,
                                [comment.id]: event.target.value,
                              }))}
                              inputProps={{ maxLength: 1000 }}
                            />
                            <Button
                              variant="contained"
                              disabled={replyingId === comment.id || !String(replyText[comment.id] || '').trim()}
                              onClick={() => submitReply(comment.id)}
                              sx={{ minWidth: { sm: 130 } }}
                            >
                              {replyingId === comment.id ? <CircularProgress size={20} color="inherit" /> : 'Publier'}
                            </Button>
                          </Stack>
                        </Box>
                      </Collapse>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Container>
  );
};

export default Comments;
